import React, { useRef } from 'react';

import Tooltip from '@atlaskit/tooltip';

import { FEATURE_FLAG } from '@qality/common-config';
import { AdjoiningTestCasesInCycle } from '@qality/common-model';
import { useIsOverflow } from '@qality/common-utils';

import {
  ContainerStyled,
  LeftSlotStyled,
  MiddleSlotStyled,
  RightSlotStyled,
  TitleLinkStyled,
  WhitespaceStyled,
} from './TestExecutionCycleNavigation.styled';
import { TestExecutionCycleNavigationButton } from './TestExecutionCycleNavigationButton';
import { useTestCycleNavigation } from './useTestCycleNavigation';

type Props = {
  testCycle?: {
    name: string;
    id: number;
  };
  adjoiningTestCasesInCycle: AdjoiningTestCasesInCycle;
};

export default function TestExecutionCycleNavigation({
  testCycle,
  adjoiningTestCasesInCycle,
}: Props): JSX.Element {
  const { navigateToTestCycle } = useTestCycleNavigation();
  const testCycleLinkRef = useRef();
  const { isOverflow } = useIsOverflow(testCycleLinkRef);

  const getIsNavigationHidden = () => {
    const isQalityFree = !FEATURE_FLAG.isPremium;
    const isExecutionAdHoc = !testCycle;
    const hasOnlyOneTestCaseInTestCycle =
      !adjoiningTestCasesInCycle.next && !adjoiningTestCasesInCycle.prev;

    return isQalityFree || isExecutionAdHoc || hasOnlyOneTestCaseInTestCycle;
  };

  if (getIsNavigationHidden()) {
    return <WhitespaceStyled data-testid='whitespace' />;
  }

  return (
    <ContainerStyled data-testid='test-cycle-navigation'>
      <LeftSlotStyled data-testid='left-slot'>
        {adjoiningTestCasesInCycle.prev && (
          <TestExecutionCycleNavigationButton
            direction='previous'
            testCaseInCycle={adjoiningTestCasesInCycle.prev}
            testCycleId={testCycle.id}
          />
        )}
      </LeftSlotStyled>
      <MiddleSlotStyled data-testid='mid-slot'>
        <Tooltip content={isOverflow && testCycle.name}>
          <TitleLinkStyled
            innerRef={testCycleLinkRef}
            onClick={() => {
              navigateToTestCycle(testCycle.id);
            }}
            data-testid='title-link'
          >
            {testCycle.name}
          </TitleLinkStyled>
        </Tooltip>
      </MiddleSlotStyled>
      <RightSlotStyled data-testid='right-slot'>
        {adjoiningTestCasesInCycle.next && (
          <TestExecutionCycleNavigationButton
            direction='next'
            testCaseInCycle={adjoiningTestCasesInCycle.next}
            testCycleId={testCycle.id}
          />
        )}
      </RightSlotStyled>
    </ContainerStyled>
  );
}
